'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UISubheader from 'UIComponents/layout/UISubheader';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import ResultsSection from 'ecosystem-marketplace-core/components/storefront/ResultsSection';
import ResultsGrid from './ResultsGrid';
import * as FilterKey from '../../../constants/search/FilterKey';
import { getFiltersHeadingI18nKey } from '../../../utils/i18n';
import { getCollectionI18nKey } from '../../../utils/i18n';
import StorefrontFilters from '../../../records/StorefrontFilters';

const getI18nKey = key => `appMarketplace.storefront.results.resultsPage.${key}`;

const SupplementalResultsSection = ({
  onResultClick,
  resultProps,
  searchResult,
  trackInteraction
}) => {
  const {
    filters,
    items,
    total
  } = searchResult;
  const resultsLength = items.size;
  const filtersHeadingI18nKey = useMemo(() => getFiltersHeadingI18nKey(filters), [filters]);
  const {
    [FilterKey.SUPER_CATEGORY]: superCategory,
    [FilterKey.CATEGORY]: category,
    [FilterKey.COLLECTION]: collection
  } = filters;
  const computedResultProps = useMemo(() => {
    const _resultProps = Object.assign({}, resultProps);

    if (superCategory) {
      _resultProps.superCategory = superCategory;
    }

    return _resultProps;
  }, [resultProps, superCategory]);
  const renderedTitle = useMemo(() => {
    if (category || superCategory) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('supplementalResults.categoryResults.headingText'),
        options: {
          category: unescapedText(filtersHeadingI18nKey)
        }
      });
    }

    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: filtersHeadingI18nKey
    });
  }, [category, filtersHeadingI18nKey, superCategory]);
  const renderedSubheading = useMemo(() => {
    if (category || superCategory) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('supplementalResults.categoryResults.subheadingText'),
        options: {
          category: unescapedText(filtersHeadingI18nKey)
        }
      });
    }

    if (collection) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: getCollectionI18nKey(collection, 'subheadingText')
      });
    }

    return null;
  }, [category, collection, filtersHeadingI18nKey, superCategory]);
  const renderedSeeAllLink = useMemo(() => {
    if (total <= resultsLength) {
      return null;
    }

    return /*#__PURE__*/_jsx(UIRouterLink, {
      onClick: () => trackInteraction('click see all supplemental results', {
        category,
        superCategory
      }, 'storefront-click-supplemental-results'),
      to: filters.delete(FilterKey.SORT_FIELD).toLocation(),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('supplementalResults.seeAllLinkText')
      })
    });
  }, [filters, resultsLength, total, category, superCategory, trackInteraction]);

  if (items.isEmpty()) {
    return null;
  }

  return /*#__PURE__*/_jsxs(ResultsSection, {
    children: [/*#__PURE__*/_jsx(UISubheader, {
      className: "p-bottom-0",
      details: renderedSubheading,
      headingLevel: "h4",
      title: renderedTitle,
      children: renderedSeeAllLink
    }), /*#__PURE__*/_jsx(ResultsGrid, {
      onResultClick: onResultClick,
      resultProps: computedResultProps,
      results: items
    })]
  });
};

SupplementalResultsSection.propTypes = {
  onResultClick: PropTypes.func.isRequired,
  resultProps: PropTypes.object,
  searchResult: PropTypes.shape({
    filters: PropTypes.instanceOf(StorefrontFilters).isRequired,
    items: ImmutablePropTypes.orderedSet.isRequired,
    total: PropTypes.number.isRequired
  }).isRequired,
  trackInteraction: PropTypes.func.isRequired
};
export default SupplementalResultsSection;